import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { Snackbar, Alert, AlertColor } from '@mui/material';

import { CONFIG } from '@/constants/config';

interface NotificationContextValue {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const show = useCallback((message: string, severity: AlertColor) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleClose = (): void => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const value: NotificationContextValue = useMemo(() => ({
    showSuccess: (m: string) => show(m, 'success'),
    showError: (m: string) => show(m, 'error'),
    showWarning: (m: string) => show(m, 'warning'),
    showInfo: (m: string) => show(m, 'info'),
  }), [show]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={CONFIG.NOTIFICATION_AUTO_HIDE_MS}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};
